import { useState } from 'react';
import { Link } from 'react-router-dom';
import { App, Button, Card, Checkbox, Modal, Popconfirm, Space, Table, Typography } from 'antd';
import { DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import type { FieldDefinitionDto, NoContactInfoItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle } from '../../components/StatCard';
import { NameCell, TableSearch } from '../../components/NameCell';
import { BulkActionsBar } from '../../components/BulkActionsBar';
import { tableLocale, tableLoading } from '../../components/states';
import { errorMessage } from '../../api/client';
import { useFields } from '../../features/fields/api';
import { useDeleteNoContactInfo, useNoContactInfo, useNoContactInfoFields, useSaveNoContactInfoFields } from '../../features/cleanup/api';
import { fmtDate, plural } from '../../utils/format';
import { EntityTag } from '../../components/EntityTag';

export function NoContactInfoPage() {
  const { message } = App.useApp();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const list = useNoContactInfo({ page, pageSize, q: q || undefined });
  const deleteAll = useDeleteNoContactInfo();
  const defs = useFields('CONTACT');
  const infoFields = useNoContactInfoFields();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const total = list.data?.total ?? 0;
  const labels = infoFieldLabels(defs.data ?? [], infoFields.data?.fields ?? []);

  const afterBulk = () => {
    setSelected([]);
    void list.refetch();
  };

  return (
    <div className="page">
      <PageHeader
        title="No contact info"
        subtitle="Contacts with nothing in any contact info field (email, phone, address, website, social profiles…), so there is no way to reach them. Choose the fields under Settings."
        extra={
          <Space>
            <Button icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)} disabled={!infoFields.data || !defs.data}>
              Settings
            </Button>
            <Popconfirm
              title={`Delete all ${plural(total, 'contact')} without contact info?`}
              description="Their notes, tasks and history are deleted too. This cannot be undone."
              okText="Delete all"
              okButtonProps={{ danger: true, loading: deleteAll.isPending }}
              onConfirm={() =>
                deleteAll
                  .mutateAsync()
                  .then((r) => {
                    message.success(`Deleted ${plural(r.deleted, 'contact')}`);
                    setSelected([]);
                    setPage(1);
                  })
                  .catch((e) => message.error(errorMessage(e)))
              }
            >
              <Button danger icon={<DeleteOutlined />} disabled={!total || !!q} title={q ? 'Clear the search to delete all' : undefined}>
                Delete all{total ? ` (${total.toLocaleString()})` : ''}
              </Button>
            </Popconfirm>
          </Space>
        }
      />
      {settingsOpen && infoFields.data && defs.data && (
        <InfoFieldsModal defs={defs.data} value={infoFields.data.fields} isDefault={infoFields.data.isDefault} onClose={() => setSettingsOpen(false)} />
      )}
      <Card
        title={
          <CardTitle
            title={list.data ? (total ? `${plural(total, 'contact')} without contact info` : 'Every contact can be reached') : 'Contacts without contact info'}
            subtitle={
              labels.length
                ? `A contact with a value in any of these counts as reachable: ${labels.slice(0, 8).join(', ')}${labels.length > 8 ? ` and ${labels.length - 8} more` : ''}.`
                : undefined
            }
          />
        }
      >
        <div className="table-toolbar">
          <div className="toolbar-group">
            <TableSearch
              value={q}
              onSearch={(v) => {
                setQ(v);
                setPage(1);
              }}
              placeholder="Search name or job title"
            />
          </div>
        </div>
        {selected.length > 0 && (
          <div className="bulk-bar" style={{ background: '#EEF2FE', borderRadius: 10, padding: '8px 12px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <BulkActionsBar selection={{ ids: selected }} count={selected.length} contactFields={defs.data ?? []} onDone={afterBulk} />
            <Space>
              <Button size="small" type="link" onClick={() => setSelected([])}>
                Clear selection
              </Button>
            </Space>
          </div>
        )}
        <Table<NoContactInfoItem>
          rowKey="id"
          loading={tableLoading(list)}
          dataSource={list.data?.items ?? []}
          locale={tableLocale(list, q ? 'No contacts match this search.' : 'Every contact has a value in at least one contact info field.')}
          rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]), preserveSelectedRowKeys: true }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (n) => plural(n, 'contact'),
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
          columns={[
            {
              title: 'Contact',
              key: 'contact',
              render: (_, c) => <NameCell name={c.name || '(no name)'} sub={c.jobTitle ?? undefined} to={`/contacts/${c.id}`} />,
            },
            {
              title: 'Organization',
              key: 'organization',
              render: (_, c) => (c.organization ? <EntityTag kind="organization" id={c.organization.id}>{c.organization.name}</EntityTag> : <Typography.Text type="secondary">—</Typography.Text>),
            },
            { title: 'Source', dataIndex: 'source', width: 140, render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
            { title: 'Created', dataIndex: 'createdAt', width: 140, render: (v: string) => fmtDate(v) },
            { title: '', key: 'open', width: 80, render: (_, c) => <Link to={`/contacts/${c.id}`}>Open</Link> },
          ]}
        />
      </Card>
    </div>
  );
}

/** Built-in columns that can count as contact info; the Email and Phone ones include the Emails / Phones lists. */
const COLUMN_KEYS = ['email', 'phone', 'mobile'];

/** Fields that can be chosen: Email, Phone and Mobile plus every custom contact field. */
function choosable(defs: FieldDefinitionDto[]) {
  return defs
    .filter((d) => (d.isSystem ? COLUMN_KEYS.includes(d.key) : true))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

function infoFieldLabels(defs: FieldDefinitionDto[], keys: string[]) {
  const byKey = new Map(choosable(defs).map((d) => [d.key, d.label]));
  return keys.map((k) => byKey.get(k)).filter((l): l is string => !!l);
}

/** Picks which contact fields count as contact info; saved for the whole workspace. */
function InfoFieldsModal({
  defs,
  value,
  isDefault,
  onClose,
}: {
  defs: FieldDefinitionDto[];
  value: string[];
  isDefault: boolean;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const save = useSaveNoContactInfoFields();
  const [chosen, setChosen] = useState<string[]>(value);
  const groups = new Map<string, FieldDefinitionDto[]>();
  for (const d of choosable(defs)) {
    const g = d.isSystem ? 'Basic' : d.group || 'Other';
    groups.set(g, [...(groups.get(g) ?? []), d]);
  }
  const run = (fields: string[] | null) =>
    save
      .mutateAsync(fields)
      .then(() => {
        message.success('Contact info fields saved');
        onClose();
      })
      .catch((e) => message.error(errorMessage(e)));

  return (
    <Modal
      open
      title="Fields that count as contact info"
      onCancel={onClose}
      width={640}
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button
            type="link"
            style={{ padding: 0 }}
            disabled={isDefault || save.isPending}
            onClick={() => void run(null)}
          >
            Reset to default
          </Button>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              loading={save.isPending}
              disabled={!chosen.length}
              onClick={() => void run(chosen)}
            >
              Save
            </Button>
          </Space>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">
        A contact is listed only when every checked field is empty. A value in any one of them means
        the contact can be reached. Email and Phone include the contact&apos;s Emails and Phones
        lists.
      </Typography.Paragraph>
      <div style={{ maxHeight: 420, overflow: 'auto' }}>
        {[...groups].map(([group, fields]) => (
          <div key={group} style={{ marginBottom: 16 }}>
            <Space style={{ marginBottom: 6 }}>
              <Typography.Text strong>{group}</Typography.Text>
              <Button
                size="small"
                type="link"
                style={{ padding: 0 }}
                onClick={() => {
                  const keys = fields.map((f) => f.key);
                  const all = keys.every((k) => chosen.includes(k));
                  setChosen(
                    all
                      ? chosen.filter((k) => !keys.includes(k))
                      : [...new Set([...chosen, ...keys])],
                  );
                }}
              >
                {fields.every((f) => chosen.includes(f.key)) ? 'Clear' : 'All'}
              </Button>
            </Space>
            <Checkbox.Group
              value={chosen.filter((k) => fields.some((f) => f.key === k))}
              onChange={(v) =>
                setChosen([
                  ...chosen.filter((k) => !fields.some((f) => f.key === k)),
                  ...(v as string[]),
                ])
              }
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 6,
              }}
              options={fields.map((f) => ({ value: f.key, label: f.label }))}
            />
          </div>
        ))}
      </div>
      {!chosen.length && <Typography.Text type="danger">Pick at least one field.</Typography.Text>}
    </Modal>
  );
}
